/** Column definitions for the import-runs grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget (the page also sets `disableColumnFilter`). Sortable
 * columns match the server's ordering whitelist (created/started/finished/
 * state/origin). Cell popovers (CellActionsPopover) carry copy actions, links
 * and quick filters — `onQuickFilter` appends an exact chip to the search.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Link as RouterLink } from 'react-router-dom';

import type { ImportRunListItem } from '@/api/importRuns';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { apiRequestsHrefForRequestId } from '@/features/api-requests/apiRequestLinks';
import { formatDuration, TsValue } from '@/features/bgtasks/taskFormat';

import { ImportStateChip, importOriginLabel, importStatusLabel, shortId } from './importRunFormat';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function copyAction(label: string, value: string): CellAction {
  return {
    key: 'copy',
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard.writeText(value),
  };
}

function filterAction(
  label: string,
  ns: string,
  value: string,
  onQuickFilter: BuildColumnsOptions['onQuickFilter'],
): CellAction {
  return {
    key: `filter-${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  };
}

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Append an exact `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Route to a run's detail dialog (keeps the grid's query string). */
  detailPath: (id: string) => string;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<ImportRunListItem>[] {
  const { showTenant, onQuickFilter, detailPath } = opts;

  const cols: GridColDef<ImportRunListItem>[] = [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (p) => (
        <Tooltip title="Open import">
          <IconButton
            size="small"
            component={RouterLink}
            to={detailPath(p.row.id)}
            aria-label="Open import"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'created_ts',
      headerName: 'Started',
      width: 190,
      filterable: false,
      renderCell: (p) => <TsValue value={p.row.created_ts} />,
    },
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 160,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.top?.name ?? '—',
      renderCell: (p) =>
        p.row.top ? (
          <WithTenantMark environment={p.row.top.environment}>
            <CellActionsPopover
              auto
              actions={[
                {
                  key: 'open',
                  label: 'Open tour operator',
                  icon: <OpenInNewIcon fontSize="small" />,
                  href: `/tour-operators/${p.row.top.id}`,
                },
                filterAction('Filter by this tenant', 'tenant', String(p.row.top.id), onQuickFilter),
              ]}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p.row.top.name}
            </CellActionsPopover>
          </WithTenantMark>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    });
  }

  cols.push(
    {
      field: 'state',
      headerName: 'Status',
      width: 180,
      filterable: false,
      valueGetter: (_v, row) => importStatusLabel(row.state),
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[
            filterAction('Filter by this status', 'status', importStatusLabel(p.row.state), onQuickFilter),
          ]}
        >
          <ImportStateChip state={p.row.state} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'origin',
      headerName: 'Origin',
      width: 150,
      filterable: false,
      valueGetter: (_v, row) => importOriginLabel(row.origin),
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[
            filterAction('Filter by this origin', 'origin', importOriginLabel(p.row.origin), onQuickFilter),
          ]}
        >
          {importOriginLabel(p.row.origin)}
        </CellActionsPopover>
      ),
    },
    {
      field: 'sources',
      headerName: 'Sources',
      flex: 1,
      minWidth: 150,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.sources.map((s) => s.code).join(', '),
      renderCell: (p) =>
        p.row.sources.length > 0 ? (
          <CellActionsPopover
            auto
            title={p.row.sources.map((s) => s.name).join(' · ')}
            actions={p.row.sources.map((s) => ({
              key: `filter-source-${s.code}`,
              label: `Filter by source ${s.code}`,
              icon: <FilterAltIcon fontSize="small" />,
              onClick: () => onQuickFilter('source', s.code),
            }))}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={mono}>
              {p.row.sources.map((s) => s.code).join(', ')}
            </Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'child_count',
      headerName: 'Runs',
      width: 90,
      filterable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => row.child_count,
      renderCell: (p) =>
        p.row.is_parent ? (
          <Tooltip title={`Group of ${p.row.child_count} child imports — expand the row`}>
            <Chip size="small" variant="outlined" label={p.row.child_count} sx={{ height: 20, fontSize: 12 }} />
          </Tooltip>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'processing_duration',
      headerName: 'Duration',
      width: 100,
      filterable: false,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => row.processing_duration,
      renderCell: (p) => (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatDuration(p.row.processing_duration)}
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'Import ID',
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={p.row.id}
          actions={[
            copyAction('Copy import ID', p.row.id),
            {
              key: 'open',
              label: 'Open import',
              icon: <OpenInNewIcon fontSize="small" />,
              href: detailPath(p.row.id),
            },
          ]}
        >
          <Box component="span" sx={mono}>{shortId(p.row.id)}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'group',
      headerName: 'Group ID',
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.group ? (
          <CellActionsPopover
            auto
            title={p.row.group}
            actions={[
              copyAction('Copy group ID', p.row.group),
              filterAction('Filter by this group', 'group', p.row.group, onQuickFilter),
            ]}
          >
            <Box component="span" sx={mono}>{shortId(p.row.group)}</Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'request_id',
      headerName: 'Request ID',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.request_id ? (
          <CellActionsPopover
            auto
            title={p.row.request_id}
            actions={[
              copyAction('Copy request ID', p.row.request_id),
              filterAction('Filter by this request', 'request', p.row.request_id, onQuickFilter),
              {
                key: 'api-requests',
                label: 'Find in API requests (±1 h)',
                icon: <OpenInNewIcon fontSize="small" />,
                href: apiRequestsHrefForRequestId(p.row.request_id, p.row.created_ts),
              },
            ]}
          >
            <Box component="span" sx={mono}>{p.row.request_id}</Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'task_id',
      headerName: 'Task ID',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.task_id || '—'}</Box>,
    },
    {
      field: 'processing_started_ts',
      headerName: 'Processing started',
      width: 190,
      filterable: false,
      // Not orderable server-side (unindexed on a large unpartitioned table).
      sortable: false,
      renderCell: (p) => <TsValue value={p.row.processing_started_ts} />,
    },
    {
      field: 'processing_finished_ts',
      headerName: 'Processing finished',
      width: 190,
      filterable: false,
      sortable: false,
      renderCell: (p) => <TsValue value={p.row.processing_finished_ts} />,
    },
    {
      field: 'url',
      headerName: 'Source URL',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ ...mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.row.url ?? undefined}>
          {p.row.url || '—'}
        </Box>
      ),
    },
  );

  return cols;
}
